import React, { useState, useEffect } from 'react';
import {
    X,
    User,
    Briefcase,
    CreditCard,
    Building2,
    Calendar,
    AlertTriangle,
    Ban,
    Trash2,
    Undo2,
    Save,
    Edit2,
    CheckCircle,
    Copy,
    ExternalLink,
    Clock,
    Eye,
    FileDown,
    FileX,
    FileInput,
    Send
} from 'lucide-react';
import { generateContractPDF, generateTerminationPDF } from '../../../utils/contractGenerator';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

/**
 * Helper component defined outside to prevent re-mounts on parent re-render.
 */
const InfoField = ({ label, value, section, field, isEditable = true, isEditing, onChange, dropdownOptions, inputType = 'text' }) => {
    if (isEditing && isEditable) {
        if (dropdownOptions) {
            return (
                <div className="space-y-1">
                    <label className="text-xs text-gray-500 font-bold uppercase">{label}</label>
                    <Select value={value} onValueChange={(val) => onChange(section, field, val)}>
                        <SelectTrigger className="bg-gray-800 border-gray-700 h-8 text-white">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 text-white">
                            {dropdownOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )
        }
        return (
            <div className="space-y-1">
                <label className="text-xs text-gray-500 font-bold uppercase">{label}</label>
                <Input
                    type={inputType}
                    value={value || ''}
                    onChange={(e) => onChange(section, field, e.target.value)}
                    className="bg-gray-800 border-gray-700 h-8 text-white focus:ring-purple-500 focus:border-purple-500"
                />
            </div>
        );
    }
    return (
        <div className="space-y-1">
            <label className="text-xs text-gray-500 font-bold uppercase">{label}</label>
            <div className="text-sm text-gray-200 font-medium break-words flex items-center gap-2">
                {value || '-'}
                {value && <Copy className="h-3 w-3 text-gray-600 cursor-pointer hover:text-white" onClick={() => {
                    navigator.clipboard.writeText(value);
                    toast.success("Copiado!");
                }} />}
            </div>
        </div>
    );
};

const ProviderDetails = ({ provider, userPayments, onClose, onUpdate, onSuspend, onReactivate, onDelete, onImpersonate, readOnly = false }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState(provider);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [confirmSuspendOpen, setConfirmSuspendOpen] = useState(false);

    // Data Request State
    const [requestDataOpen, setRequestDataOpen] = useState(false);
    const [requestTypes, setRequestTypes] = useState({
        personal: false,
        business: false,
        financial: false,
        contract: false,
        profile: false
    });

    // Reset types when opening modal
    useEffect(() => {
        if (requestDataOpen) {
            setRequestTypes({
                personal: false,
                business: false,
                financial: false,
                contract: false,
                profile: false
            });
        }
    }, [requestDataOpen]);

    const handleRequestDataSubmit = async () => {
        const types = Object.keys(requestTypes).filter(key => requestTypes[key]);
        if (types.length === 0) {
            toast.error("Selecione pelo menos um tipo de dado.");
            return;
        }

        try {
            await onUpdate(provider.discordId, {
                staffDetails: {
                    ...provider.staffDetails,
                    dataUpdateRequest: {
                        requestedAt: new Date().toISOString(),
                        types: types,
                        status: 'pending'
                    }
                }
            });
            toast.success("Solicitação de dados enviada com sucesso!");
            setRequestDataOpen(false);
            setRequestTypes({ personal: false, business: false, financial: false, contract: false, profile: false });
        } catch (error) {
            console.error("Error requesting data:", error);
            toast.error("Erro ao enviar solicitação.");
        }
    };

    // Initial check to ensure nested objects exist for editing
    const ensureDataStructure = (data) => ({
        ...data,
        staffDetails: {
            ...data.staffDetails,
            personalData: data.staffDetails?.personalData || {},
            businessData: data.staffDetails?.businessData || {},
            bankingData: data.staffDetails?.bankingData || {},
            contractData: data.staffDetails?.contractData || {}
        }
    });

    const handleStartEdit = () => {
        setEditedData(ensureDataStructure(provider));
        setIsEditing(true);
    };

    const handleSave = async () => {
        try {
            await onUpdate(provider.discordId, {
                staffDetails: editedData.staffDetails,
                name: editedData.staffDetails.personalData.fullName,
                accessLevel: editedData.accessLevel,
                email: editedData.email // Ensure email is synced if changed at top level
            });
            setIsEditing(false);
            // toast handled by parent
        } catch (error) {
            toast.error("Erro ao salvar dados.");
        }
    };

    const handleChange = (section, field, value) => {
        if (section === 'root') {
            setEditedData(prev => ({
                ...prev,
                [field]: value
            }));
        } else {
            setEditedData(prev => ({
                ...prev,
                staffDetails: {
                    ...prev.staffDetails,
                    [section]: {
                        ...prev.staffDetails[section],
                        [field]: value
                    }
                }
            }));
        }
    };

    const formatDate = (dateString, includeTime = false) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return includeTime
            ? date.toLocaleString('pt-BR')
            : date.toLocaleDateString('pt-BR');
    };

    const formatCurrency = (value) => {
        if (!value) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    // Calculate generic stats
    const stats = provider.stats || {
        resolvedTickets: 0,
        pendingTickets: 0,
        approvedPayments: 0,
        totalPaid: 0,
        contractMonths: 0
    };

    // Calculate Days Remaining
    const calculateDaysRemaining = () => {
        if (!provider.staffDetails?.contractData?.endDate) {
            // Fallback: calculate from start date + months if available
            if (provider.staffDetails?.contractData?.startDate && provider.staffDetails?.contractData?.contractMonths) {
                const start = new Date(provider.staffDetails.contractData.startDate);
                const duration = parseInt(provider.staffDetails.contractData.contractDuration || provider.staffDetails.contractData.contractMonths);
                const unit = provider.staffDetails.contractData.contractUnit || 'months';
                const end = new Date(start);

                if (unit === 'days') {
                    end.setDate(end.getDate() + duration);
                } else {
                    end.setMonth(end.getMonth() + duration);
                }

                const diffTime = end - new Date();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                return diffDays > 0 ? diffDays : 0;
            }
            return 0;
        }
        const end = new Date(provider.staffDetails.contractData.endDate);
        const diffTime = end - new Date();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const daysRemaining = calculateDaysRemaining();

    const roleOptions = [
        { value: 'suporte', label: 'Suporte' },
        { value: 'suporte_compras', label: 'Suporte Compras' },
        { value: 'suporte_descontos', label: 'Suporte Descontos' },
        { value: 'moderador', label: 'Moderador' },
        { value: 'super_moderador', label: 'Super Moderador' },
        { value: 'programador', label: 'Programador' },
        { value: 'head_suporte', label: 'Head Suporte' },
        { value: 'head_moderacao', label: 'Head Moderação' },
        { value: 'head_staff', label: 'Head Staff' },
        { value: 'analista_qualidade', label: 'Analista de Qualidade' },
        { value: 'departamento_juridico', label: 'Departamento Jurídico' },
        { value: 'criador_de_conteudo', label: 'Criador de Conteúdo' },
        { value: 'admin', label: 'Administrador' }
    ];

    return (
        <div
            className="fixed inset-y-0 right-0 w-full md:w-[600px] lg:w-[800px] bg-[#09090b] shadow-2xl border-l border-white/10 transform transition-transform duration-300 ease-in-out z-40 flex flex-col"
        >
            {/* Header */}
            <div className="p-6 border-b border-white/10 flex items-start justify-between bg-black/20">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 p-[2px]">
                        <div className="w-full h-full rounded-full bg-black overflow-hidden">
                            {provider.avatar ? (
                                <img
                                    src={`https://cdn.discordapp.com/avatars/${provider.discordId}/${provider.avatar}.png`}
                                    alt={provider.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <User className="w-full h-full p-3 text-gray-400" />
                            )}
                        </div>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                            {provider.staffDetails?.personalData?.fullName || provider.name}
                            {provider.staffDetails?.isSuspended && (
                                <Badge variant="destructive" className="ml-2">SUSPENSO</Badge>
                            )}
                        </h2>
                        <div className="flex items-center gap-2 text-gray-400 text-sm mt-1">
                            <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                                {provider.accessLevel}
                            </Badge>
                            <span>•</span>
                            <span>{provider.staffDetails?.contractData?.codename || provider.staffDetails?.preStaffData?.codename || 'Sem Codinome'}</span>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    {isEditing ? (
                        <>
                            <Button variant="ghost" size="icon" onClick={() => setIsEditing(false)}>
                                <Undo2 className="h-5 w-5 text-gray-400" />
                            </Button>
                            <Button variant="default" size="icon" className="bg-green-600 hover:bg-green-700" onClick={handleSave}>
                                <Save className="h-5 w-5 text-white" />
                            </Button>
                        </>
                    ) : (
                        <>
                            {!readOnly && (
                                <Button variant="ghost" size="icon" onClick={() => onImpersonate(provider.discordId)} title="Visualizar Painel">
                                    <Eye className="h-5 w-5 text-purple-400" />
                                </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => generateContractPDF(provider)} title="Baixar Contrato">
                                <FileDown className="h-5 w-5 text-green-400" />
                            </Button>
                            {provider.staffDetails?.contractData?.terminationSignedAt && (
                                <Button variant="ghost" size="icon" onClick={() => generateTerminationPDF(provider)} title="Baixar Termo de Quitação">
                                    <FileX className="h-5 w-5 text-red-400" />
                                </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => setRequestDataOpen(true)} title="Solicitar Dados">
                                <FileInput className="h-5 w-5 text-yellow-400" />
                            </Button>
                            {!readOnly && (
                                <Button variant="ghost" size="icon" onClick={handleStartEdit} title="Editar Tudo">
                                    <Edit2 className="h-5 w-5 text-blue-400" />
                                </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={onClose}>
                                <X className="h-6 w-6" />
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Content using Tabs */}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                    <div className="px-6 pt-4 border-b border-white/5">
                        <TabsList className="bg-transparent w-full justify-start gap-4">
                            <TabsTrigger value="overview" className="text-gray-400 hover:text-white data-[state=active]:bg-purple-500/10 data-[state=active]:text-purple-400 border-b-2 border-transparent data-[state=active]:border-purple-500 rounded-none px-4 py-2">Visão Geral</TabsTrigger>
                            <TabsTrigger value="personal" className="text-gray-400 hover:text-white data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-400 border-b-2 border-transparent data-[state=active]:border-blue-500 rounded-none px-4 py-2">Pessoal</TabsTrigger>
                            <TabsTrigger value="company" className="text-gray-400 hover:text-white data-[state=active]:bg-orange-500/10 data-[state=active]:text-orange-400 border-b-2 border-transparent data-[state=active]:border-orange-500 rounded-none px-4 py-2">Empresa</TabsTrigger>
                            <TabsTrigger value="financial" className="text-gray-400 hover:text-white data-[state=active]:bg-green-500/10 data-[state=active]:text-green-400 border-b-2 border-transparent data-[state=active]:border-green-500 rounded-none px-4 py-2">Financeiro</TabsTrigger>
                            <TabsTrigger value="contract_history" className="text-gray-400 hover:text-white data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-400 border-b-2 border-transparent data-[state=active]:border-blue-500 rounded-none px-4 py-2">Contrato</TabsTrigger>
                            <TabsTrigger value="history" className="text-gray-400 hover:text-white data-[state=active]:bg-gray-500/10 data-[state=active]:text-gray-400 border-b-2 border-transparent data-[state=active]:border-gray-500 rounded-none px-4 py-2">Histórico</TabsTrigger>
                        </TabsList>
                    </div>

                    <ScrollArea className="flex-1 min-h-0">
                        <div className="p-6 space-y-6">

                            {/* OVERVIEW TAB */}
                            <TabsContent value="overview" className="mt-0 space-y-6">
                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="p-4 rounded-xl bg-purple-500/5 border border-purple-500/10">
                                        <p className="text-[10px] text-purple-400 font-bold uppercase tracking-wider">Fim do Contrato</p>
                                        <div className="flex items-baseline gap-1 mt-1">
                                            <p className="text-2xl font-bold text-white">{daysRemaining}</p>
                                            <span className="text-xs text-purple-400/70">dias</span>
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10">
                                        <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wider">Tickets Resolvidos</p>
                                        <p className="text-2xl font-bold text-white mt-1">{stats.resolvedTickets}</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/10">
                                        <p className="text-[10px] text-green-400 font-bold uppercase tracking-wider">Pgts Aprovados</p>
                                        <p className="text-2xl font-bold text-white mt-1">{stats.approvedPayments}</p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/10">
                                        <p className="text-[10px] text-yellow-400 font-bold uppercase tracking-wider">Total Recebido</p>
                                        <p className="text-xl font-bold text-white mt-1 truncate" title={formatCurrency(stats.totalPaid)}>{formatCurrency(stats.totalPaid)}</p>
                                    </div>
                                </div>

                                {/* Main Info Summary */}
                                <Card className="bg-white/5 border-white/10">
                                    <CardHeader>
                                        <CardTitle className="text-lg text-white flex items-center gap-2">
                                            <User className="h-5 w-5 text-purple-400" />
                                            Resumo do Cadastro
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <InfoField
                                            label="Codinome"
                                            value={editedData.staffDetails?.contractData?.codename || editedData.staffDetails?.preStaffData?.codename}
                                            section="contractData" field="codename"
                                            isEditing={isEditing} onChange={handleChange}
                                        />
                                        <InfoField
                                            label="Nome Completo"
                                            value={editedData.staffDetails?.personalData?.fullName}
                                            section="personalData" field="fullName"
                                            isEditing={isEditing} onChange={handleChange}
                                        />
                                        <InfoField
                                            label="Função (Cargo)"
                                            value={editedData.accessLevel}
                                            section="root" field="accessLevel"
                                            isEditing={isEditing} onChange={handleChange}
                                            dropdownOptions={roleOptions}
                                        />
                                        <InfoField
                                            label="E-mail (Discord)"
                                            value={editedData.email || editedData.discordData?.email}
                                            section="root" field="email"
                                            isEditing={isEditing} onChange={handleChange}
                                            editable={false} // Email typically blocked or from discord
                                        />
                                        <InfoField
                                            label="Último Login"
                                            value={formatDate(provider.lastLogin, true)}
                                            isEditable={false} // Read-only
                                            isEditing={isEditing}
                                        />
                                        <InfoField
                                            label="Status"
                                            value={provider.staffDetails?.isSuspended ? 'SUSPENSO' : 'ATIVO'}
                                            isEditable={false}
                                            isEditing={isEditing}
                                        />
                                    </CardContent>
                                </Card>

                                {/* Contract Summary */}
                                <Card className="bg-white/5 border-white/10">
                                    <CardHeader>
                                        <CardTitle className="text-lg text-white flex items-center gap-2">
                                            <Briefcase className="h-5 w-5 text-blue-400" />
                                            Contrato Vigente
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <InfoField
                                            label="Valor Mensal"
                                            value={editedData.staffDetails?.contractData?.contractValue}
                                            section="contractData" field="contractValue"
                                            isEditing={isEditing} onChange={handleChange}
                                            inputType="number"
                                        />
                                        <InfoField
                                            label={`Prazo (${editedData.staffDetails?.contractData?.contractUnit === 'days' ? 'Dias' : 'Meses'})`}
                                            value={editedData.staffDetails?.contractData?.contractDuration || editedData.staffDetails?.contractData?.contractMonths}
                                            section="contractData" field="contractDuration"
                                            isEditing={isEditing} onChange={handleChange}
                                            inputType="number"
                                        />
                                        <InfoField
                                            label="Descrição"
                                            value={editedData.staffDetails?.contractData?.serviceDescription}
                                            section="contractData" field="serviceDescription"
                                            isEditing={isEditing} onChange={handleChange}
                                        />
                                        <InfoField
                                            label="Data de Término"
                                            value={editedData.staffDetails?.contractData?.endDate ? new Date(editedData.staffDetails.contractData.endDate).toISOString().split('T')[0] : ''}
                                            section="contractData" field="endDate"
                                            isEditing={isEditing} onChange={handleChange}
                                            inputType="date"
                                        />
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* PERSONAL TAB */}
                            <TabsContent value="personal" className="mt-0">
                                <Card className="bg-white/5 border-white/10">
                                    <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <InfoField
                                            label="Nome Completo"
                                            value={editedData.staffDetails?.personalData?.fullName}
                                            section="personalData" field="fullName"
                                            isEditing={isEditing} onChange={handleChange}
                                        />
                                        <InfoField
                                            label="CPF"
                                            value={editedData.staffDetails?.personalData?.cpf}
                                            section="personalData" field="cpf"
                                            isEditing={isEditing} onChange={handleChange}
                                        />
                                        <InfoField
                                            label="Data de Nascimento"
                                            value={editedData.staffDetails?.personalData?.birthDate}
                                            section="personalData" field="birthDate"
                                            isEditing={isEditing} onChange={handleChange}
                                            inputType="date"
                                        />
                                        <InfoField
                                            label="Telefone"
                                            value={editedData.staffDetails?.personalData?.phone}
                                            section="personalData" field="phone"
                                            isEditing={isEditing} onChange={handleChange}
                                        />
                                        <InfoField
                                            label="E-mail"
                                            value={editedData.staffDetails?.personalData?.email}
                                            section="personalData" field="email"
                                            isEditing={isEditing} onChange={handleChange}
                                        />
                                        <InfoField
                                            label="Discord ID"
                                            value={provider.discordId}
                                            isEditable={false}
                                            isEditing={isEditing}
                                        />
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* COMPANY TAB */}
                            <TabsContent value="company" className="mt-0">
                                <Card className="bg-white/5 border-white/10">
                                    <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <InfoField label="Razão Social" value={editedData.staffDetails?.businessData?.businessName} section="businessData" field="businessName" isEditing={isEditing} onChange={handleChange} />
                                        <InfoField label="CNPJ" value={editedData.staffDetails?.businessData?.cnpj} section="businessData" field="cnpj" isEditing={isEditing} onChange={handleChange} />
                                        <InfoField label="CNAE Principal" value={editedData.staffDetails?.businessData?.primaryCnae} section="businessData" field="primaryCnae" isEditing={isEditing} onChange={handleChange} />
                                        <InfoField label="Data de Abertura" value={editedData.staffDetails?.businessData?.openingDate} section="businessData" field="openingDate" isEditing={isEditing} onChange={handleChange} inputType="date" />
                                        <div className="md:col-span-2">
                                            <InfoField label="Endereço Completo" value={editedData.staffDetails?.businessData?.fullAddress} section="businessData" field="fullAddress" isEditing={isEditing} onChange={handleChange} />
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* FINANCIAL TAB */}
                            <TabsContent value="financial" className="mt-0 space-y-6">
                                <Card className="bg-white/5 border-white/10">
                                    <CardHeader>
                                        <CardTitle className="text-lg text-white flex items-center gap-2">
                                            <Building2 className="h-5 w-5 text-green-400" />
                                            Dados Bancários
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <InfoField label="Banco" value={editedData.staffDetails?.bankingData?.bank} section="bankingData" field="bank" isEditing={isEditing} onChange={handleChange} />
                                        <InfoField label="Tipo de Conta" value={editedData.staffDetails?.bankingData?.accountType} section="bankingData" field="accountType" isEditing={isEditing} onChange={handleChange} />
                                        <InfoField label="Agência" value={editedData.staffDetails?.bankingData?.branch} section="bankingData" field="branch" isEditing={isEditing} onChange={handleChange} />
                                        <InfoField label="Conta" value={editedData.staffDetails?.bankingData?.account} section="bankingData" field="account" isEditing={isEditing} onChange={handleChange} />
                                        <InfoField label="Titular" value={editedData.staffDetails?.bankingData?.holderName} section="bankingData" field="holderName" isEditing={isEditing} onChange={handleChange} />
                                        <div className="p-4 bg-gray-900 rounded-lg border border-white/10">
                                            <InfoField label="Chave PIX" value={editedData.staffDetails?.bankingData?.pixKey} section="bankingData" field="pixKey" isEditing={isEditing} onChange={handleChange} />
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* CONTRACT HISTORY TAB */}
                            <TabsContent value="contract_history" className="mt-0">
                                <div className="space-y-6">
                                    {/* Contract Details Card */}
                                    <Card className="bg-white/5 border-white/10">
                                        <CardHeader>
                                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                                <Briefcase className="h-5 w-5 text-blue-400" />
                                                Detalhes do Contrato
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <InfoField
                                                label="Valor Mensal"
                                                value={editedData.staffDetails?.contractData?.contractValue}
                                                section="contractData" field="contractValue"
                                                isEditing={isEditing} onChange={handleChange}
                                                inputType="number"
                                            />
                                            <InfoField
                                                label={`Prazo (${editedData.staffDetails?.contractData?.contractUnit === 'days' ? 'Dias' : 'Meses'})`}
                                                value={editedData.staffDetails?.contractData?.contractDuration || editedData.staffDetails?.contractData?.contractMonths}
                                                section="contractData" field="contractDuration"
                                                isEditing={isEditing} onChange={handleChange}
                                                inputType="number"
                                            />
                                            <InfoField
                                                label="Data de Término"
                                                value={editedData.staffDetails?.contractData?.endDate ? new Date(editedData.staffDetails.contractData.endDate).toISOString().split('T')[0] : ''}
                                                section="contractData" field="endDate"
                                                isEditing={isEditing} onChange={handleChange}
                                                inputType="date"
                                            />
                                        </CardContent>
                                    </Card>

                                    {/* Timeline Card */}
                                    <div className="bg-gray-900/50 border border-white/10 rounded-xl p-6">
                                        <h3 className="text-white font-bold flex items-center gap-2 mb-6">
                                            <Clock className="h-5 w-5 text-purple-400" />
                                            Histórico de Assinatura
                                        </h3>

                                        <div className="relative space-y-8 pl-8 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] before:bg-gray-800">
                                            {/* Event 1: Started */}
                                            <div className="relative">
                                                <div className={`absolute -left-[39px] h-6 w-6 rounded-full border-2 flex items-center justify-center bg-gray-900 ${provider.staffDetails?.contractData?.onboardingStartedAt ? 'border-green-500 text-green-500' : 'border-gray-700 text-gray-700'}`}>
                                                    <div className={`h-2 w-2 rounded-full ${provider.staffDetails?.contractData?.onboardingStartedAt ? 'bg-green-500' : 'bg-gray-700'}`} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-white">Iniciou o Onboarding</p>
                                                    <p className="text-xs text-gray-400">Clique em "Começar"</p>
                                                    <p className="text-xs text-gray-500 mt-1 font-mono">
                                                        {formatDate(provider.staffDetails?.contractData?.onboardingStartedAt, true)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Event 2: Agreed */}
                                            <div className="relative">
                                                <div className={`absolute -left-[39px] h-6 w-6 rounded-full border-2 flex items-center justify-center bg-gray-900 ${provider.staffDetails?.contractData?.termsAgreedAt ? 'border-green-500 text-green-500' : 'border-gray-700 text-gray-700'}`}>
                                                    <div className={`h-2 w-2 rounded-full ${provider.staffDetails?.contractData?.termsAgreedAt ? 'bg-green-500' : 'bg-gray-700'}`} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-white">Aceitou os Termos</p>
                                                    <p className="text-xs text-gray-400">Clique em "Concordar e Prosseguir" (Após download)</p>
                                                    <p className="text-xs text-gray-500 mt-1 font-mono">
                                                        {formatDate(provider.staffDetails?.contractData?.termsAgreedAt, true)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Event 3: Completed Validation */}
                                            <div className="relative">
                                                <div className={`absolute -left-[39px] h-6 w-6 rounded-full border-2 flex items-center justify-center bg-gray-900 ${provider.staffDetails?.contractData?.onboardingCompletedAt ? 'border-green-500 text-green-500' : 'border-gray-700 text-gray-700'}`}>
                                                    <div className={`h-2 w-2 rounded-full ${provider.staffDetails?.contractData?.onboardingCompletedAt ? 'bg-green-500' : 'bg-gray-700'}`} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-white">Concluiu Validação (Selfie)</p>
                                                    <p className="text-xs text-gray-400">Envio de Selfie + Código</p>
                                                    <p className="text-xs text-gray-500 mt-1 font-mono">
                                                        {formatDate(provider.staffDetails?.contractData?.onboardingCompletedAt, true)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Event 4: Signed via DocuSign */}
                                            <div className="relative">
                                                <div className={`absolute -left-[39px] h-6 w-6 rounded-full border-2 flex items-center justify-center bg-gray-900 ${provider.staffDetails?.contractData?.docusignSignedAt ? 'border-purple-500 text-purple-500' : 'border-gray-700 text-gray-700'}`}>
                                                    <CheckCircle className={`h-4 w-4 ${provider.staffDetails?.contractData?.docusignSignedAt ? 'text-purple-500' : 'text-gray-700'}`} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-white">Assinatura DocuSign Confirmada</p>
                                                    <p className="text-xs text-gray-400">Clique em "Já Assinei"</p>
                                                    <p className="text-xs text-gray-500 mt-1 font-mono">
                                                        {formatDate(provider.staffDetails?.contractData?.docusignSignedAt, true)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* TERMINATION EVENTS */}
                                            {(provider.staffDetails?.contractData?.terminationStartedAt || provider.staffDetails?.contractData?.terminationSignedAt) && (
                                                <>
                                                    <div className="w-full border-t border-gray-800 my-4 opacity-50"></div>

                                                    {/* Event 5: Termination Started */}
                                                    <div className="relative">
                                                        <div className={`absolute -left-[39px] h-6 w-6 rounded-full border-2 flex items-center justify-center bg-gray-900 ${provider.staffDetails?.contractData?.terminationStartedAt ? 'border-red-500 text-red-500' : 'border-gray-700 text-gray-700'}`}>
                                                            <div className={`h-2 w-2 rounded-full ${provider.staffDetails?.contractData?.terminationStartedAt ? 'bg-red-500' : 'bg-gray-700'}`} />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-white">Iniciou Desligamento</p>
                                                            <p className="text-xs text-gray-400">Notificação de fim de contrato</p>
                                                            <p className="text-xs text-gray-500 mt-1 font-mono">
                                                                {formatDate(provider.staffDetails?.contractData?.terminationStartedAt, true)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Event 6: Termination Viewed */}
                                                    <div className="relative">
                                                        <div className={`absolute -left-[39px] h-6 w-6 rounded-full border-2 flex items-center justify-center bg-gray-900 ${provider.staffDetails?.contractData?.terminationViewedAt ? 'border-red-500 text-red-500' : 'border-gray-700 text-gray-700'}`}>
                                                            <div className={`h-2 w-2 rounded-full ${provider.staffDetails?.contractData?.terminationViewedAt ? 'bg-red-500' : 'bg-gray-700'}`} />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-white">Revisou Termo de Quitação</p>
                                                            <p className="text-xs text-gray-400">Leitura do documento de saída</p>
                                                            <p className="text-xs text-gray-500 mt-1 font-mono">
                                                                {formatDate(provider.staffDetails?.contractData?.terminationViewedAt, true)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Event 7: Termination Signed */}
                                                    <div className="relative">
                                                        <div className={`absolute -left-[39px] h-6 w-6 rounded-full border-2 flex items-center justify-center bg-gray-900 ${provider.staffDetails?.contractData?.terminationSignedAt ? 'border-red-500 text-red-500' : 'border-gray-700 text-gray-700'}`}>
                                                            <FileX className={`h-4 w-4 ${provider.staffDetails?.contractData?.terminationSignedAt ? 'text-red-500' : 'text-gray-700'}`} />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-white">Desligamento Confirmado</p>
                                                            <p className="text-xs text-gray-400">Assinatura do termo finalizada</p>
                                                            <p className="text-xs text-gray-500 mt-1 font-mono">
                                                                {formatDate(provider.staffDetails?.contractData?.terminationSignedAt, true)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* HISTORY TAB */}
                            <TabsContent value="history" className="mt-0">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-white font-bold flex items-center gap-2">
                                            <CreditCard className="h-5 w-5 text-green-400" />
                                            Histórico de Pagamentos e Eventos
                                        </h3>
                                        <Badge variant="outline" className="border-green-500/30 text-green-400">
                                            Total Recebido: {formatCurrency(stats.totalPaid)}
                                        </Badge>
                                    </div>

                                    {provider.staffDetails?.contractData?.terminationSignedAt && (
                                        <div className="bg-red-500/5 border border-red-500/10 p-4 rounded-lg flex justify-between items-center transform transition-all hover:bg-red-500/10">
                                            <div>
                                                <p className="text-white font-medium flex items-center gap-2">
                                                    <FileX className="h-4 w-4 text-red-500" />
                                                    Contrato Encerrado
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Clock className="h-3 w-3 text-gray-500" />
                                                    <p className="text-xs text-gray-400">
                                                        {formatDate(provider.staffDetails?.contractData?.terminationSignedAt, true)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <Badge variant="outline" className="border-red-500/30 text-red-500">
                                                    Finalizado
                                                </Badge>
                                            </div>
                                        </div>
                                    )}

                                    {userPayments && userPayments.length > 0 ? (
                                        <div className="space-y-3">
                                            {userPayments.map(payment => (
                                                <div key={payment.id} className="bg-white/5 border border-white/10 p-4 rounded-lg flex justify-between items-center transform transition-all hover:bg-white/10">
                                                    <div>
                                                        <p className="text-white font-medium">{payment.description || 'Solicitação de Pagamento'}</p>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <Clock className="h-3 w-3 text-gray-500" />
                                                            <p className="text-xs text-gray-400">
                                                                {payment.requestDate ? new Date(payment.requestDate).toLocaleString('pt-BR') : '-'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-green-400 font-mono font-bold text-lg">{formatCurrency(payment.totalValue)}</p>
                                                        <Badge variant="outline" className={`mt-1 text-[10px] uppercase ${payment.status === 'approved' ? 'border-green-500/30 text-green-500' :
                                                            payment.status === 'paid' ? 'border-blue-500/30 text-blue-500' :
                                                                'border-yellow-500/30 text-yellow-500'
                                                            }`}>
                                                            {payment.status === 'approved' ? 'Aprovado' :
                                                                payment.status === 'paid' ? 'Pago' :
                                                                    'Pendente'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        !provider.staffDetails?.contractData?.terminationSignedAt && (
                                            <div className="text-center py-8 text-gray-500 bg-white/5 rounded-xl border border-white/5">
                                                Nenhum registro de pagamento encontrado.
                                            </div>
                                        )
                                    )}
                                </div>
                            </TabsContent>
                        </div>
                    </ScrollArea>
                </Tabs>
            </div>

            {/* Footer Actions - "Zona de Perigo" */}
            <div className="p-6 border-t border-white/10 bg-black/40">
                <div className="flex flex-wrap gap-4 justify-between items-center">
                    <div className="flex flex-wrap gap-2">
                        {!readOnly && (
                            <Button
                                variant="outline"
                                className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border-purple-500/30"
                                onClick={() => onImpersonate(provider.discordId)}
                            >
                                <Eye className="h-4 w-4 mr-2" />
                                Visualizar Painel
                            </Button>
                        )}

                        <Button
                            variant="outline"
                            className="bg-green-500/10 hover:bg-green-500/20 text-green-400 border-green-500/30"
                            onClick={() => generateContractPDF(provider)}
                        >
                            <FileDown className="h-4 w-4 mr-2" />
                            Baixar Contrato
                        </Button>


                        {(provider.staffDetails?.contractData?.terminationSignedAt ||
                            provider.staffDetails?.contractData?.status === 'terminated' ||
                            provider.staffDetails?.contractData?.terminationDate) && (
                                <Button
                                    variant="outline"
                                    className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/30"
                                    onClick={() => generateTerminationPDF(provider)}
                                >
                                    <FileX className="h-4 w-4 mr-2" />
                                    Baixar Quitação
                                </Button>
                            )}

                        {(provider.staffDetails?.contractData?.liquidationSignedAt) && (
                            <Button
                                variant="outline"
                                className="bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border-orange-500/30"
                                onClick={() => generateTerminationPDF(provider, 'liquidation')} // We can pass a flag if needed, or just reuse since text is same. The user said text is same. We might want to rename the file.
                            >
                                <FileX className="h-4 w-4 mr-2" />
                                Baixar Liquidação
                            </Button>
                        )}


                        {!readOnly && (
                            <>
                                <Button
                                    variant="outline"
                                    className="bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border-orange-500/30"
                                    onClick={() => onUpdate(provider.discordId, {
                                        staffDetails: {
                                            ...provider.staffDetails,
                                            contractData: {
                                                ...provider.staffDetails.contractData,
                                                liquidationPending: true,
                                                liquidationSignedAt: null // Reset if we want to force new signature? Usually yes.
                                            }
                                        }
                                    })}
                                >
                                    <AlertTriangle className="h-4 w-4 mr-2" />
                                    Liquidação
                                </Button>

                                <Button
                                    variant="outline"
                                    className="bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                                    onClick={() => setRequestDataOpen(true)}
                                >
                                    <FileInput className="h-4 w-4 mr-2" />
                                    Solicitar Dados
                                </Button>

                                {provider.staffDetails?.isSuspended ? (
                                    <Button
                                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0 shadow-lg shadow-green-900/20"
                                        onClick={() => onReactivate(provider.discordId)}
                                    >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Reativar Acesso
                                    </Button>
                                ) : (
                                    <Button
                                        className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white border-0 shadow-lg shadow-yellow-900/20"
                                        onClick={() => setConfirmSuspendOpen(true)}
                                    >
                                        <Ban className="h-4 w-4 mr-2" />
                                        Suspender Acesso
                                    </Button>
                                )}

                                <Button
                                    className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white border-0 shadow-lg shadow-red-900/20"
                                    onClick={() => setConfirmDeleteOpen(true)}
                                >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Excluir Prestador
                                </Button>
                            </>
                        )}
                    </div>
                    <div className="text-xs text-gray-600">
                        ID: {provider.discordId}
                    </div>
                </div>
            </div>

            {/* Confirmation Dialogs */}
            <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
                <DialogContent className="bg-gray-900 border-white/10 text-white">
                    <DialogHeader>
                        <DialogTitle>Excluir Prestador?</DialogTitle>
                        <DialogDescription>
                            Esta ação removerá permanentemente o prestador do sistema (incluindo pré-registros e cadastros pendentes). Não pode ser desfeita.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmDeleteOpen(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={() => { onDelete(provider.discordId); setConfirmDeleteOpen(false); }}>
                            Confirmar Exclusão
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={confirmSuspendOpen} onOpenChange={setConfirmSuspendOpen}>
                <DialogContent className="bg-gray-900 border-white/10 text-white">
                    <DialogHeader>
                        <DialogTitle>Suspender Acesso?</DialogTitle>
                        <DialogDescription>
                            O prestador perderá acesso imediato ao painel da staff.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmSuspendOpen(false)}>Cancelar</Button>
                        <Button className="bg-yellow-600 hover:bg-yellow-700 text-white" onClick={() => { onSuspend(provider.discordId); setConfirmSuspendOpen(false); }}>
                            Confirmar Suspensão
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={requestDataOpen} onOpenChange={setRequestDataOpen}>
                <DialogContent className="bg-[#09090b] border border-gray-800 text-white sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle>Solicitar Atualização de Dados</DialogTitle>
                        <DialogDescription>
                            Selecione os dados que o prestador deve atualizar.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="type-personal"
                                checked={requestTypes.personal}
                                onChange={(e) => setRequestTypes(prev => ({ ...prev, personal: e.target.checked }))}
                                className="w-4 h-4 rounded border-gray-800 bg-gray-900 text-purple-600 focus:ring-purple-500"
                            />
                            <label htmlFor="type-personal" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Dados Pessoais
                            </label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="type-business"
                                checked={requestTypes.business}
                                onChange={(e) => setRequestTypes(prev => ({ ...prev, business: e.target.checked }))}
                                className="w-4 h-4 rounded border-gray-800 bg-gray-900 text-purple-600 focus:ring-purple-500"
                            />
                            <label htmlFor="type-business" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Dados da Empresa
                            </label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="type-financial"
                                checked={requestTypes.financial}
                                onChange={(e) => setRequestTypes(prev => ({ ...prev, financial: e.target.checked }))}
                                className="w-4 h-4 rounded border-gray-800 bg-gray-900 text-purple-600 focus:ring-purple-500"
                            />
                            <label htmlFor="type-financial" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Dados Bancários
                            </label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="type-contract"
                                checked={requestTypes.contract}
                                onChange={(e) => setRequestTypes(prev => ({ ...prev, contract: e.target.checked }))}
                                className="w-4 h-4 rounded border-gray-800 bg-gray-900 text-purple-600 focus:ring-purple-500"
                            />
                            <label htmlFor="type-contract" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Dados do Contrato
                            </label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="type-profile"
                                checked={requestTypes.profile}
                                onChange={(e) => setRequestTypes(prev => ({ ...prev, profile: e.target.checked }))}
                                className="w-4 h-4 rounded border-gray-800 bg-gray-900 text-purple-600 focus:ring-purple-500"
                            />
                            <label htmlFor="type-profile" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Perfil (Codinome)
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRequestDataOpen(false)} className="border-gray-700 text-gray-300 hover:bg-gray-800">
                            Cancelar
                        </Button>
                        <Button onClick={handleRequestDataSubmit} className="bg-yellow-600 hover:bg-yellow-700 text-white">
                            <Send className="w-4 h-4 mr-2" />
                            Enviar Solicitação
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProviderDetails;
